// ===================
// Assistant dock (R4 §2.8)
// ===================
//
// One right-hand assistant panel, mounted once in AppLayout. Which
// conversation it hosts depends on the route: a bid page hosts that bid's
// conversation (tender-chat-store), every other page hosts the org assistant
// (assistant-store). The five editor pages keep their own UnifiedAiPanel and
// the Assistant page is the org assistant itself, so the dock is hidden there.

export const MIN_DOCK_WIDTH = 320
export const DEFAULT_DOCK_WIDTH = 400
export const MAX_DOCK_WIDTH = 560
/** The dock never takes more than this share of the window. */
export const MAX_DOCK_VIEWPORT_SHARE = 0.4

/** Below this width the dock never pushes the page (plan §2.8: overlay under 1280). */
export const DOCKED_MIN_VIEWPORT = 1280
/** The dock pushes the page only if the page keeps at least this much width. */
export const MIN_PAGE_CONTENT_WIDTH = 720
/** App rail width (`SIDEBAR_WIDTH` 4.5rem in `ui/sidebar.tsx`). */
export const APP_RAIL_WIDTH = 72
/** Bid menu widths (`w-52` / `w-12` in `BidMenu.tsx`). */
export const BID_MENU_EXPANDED_WIDTH = 208
export const BID_MENU_COLLAPSED_WIDTH = 48
/** Below this width the dock is full-screen. */
export const FULLSCREEN_MAX_VIEWPORT = 767

export type AssistantDockMode =
  | { kind: 'hidden' }
  | { kind: 'bid'; bidId: string }
  | { kind: 'org' }

export type AssistantDockLayout = 'docked' | 'overlay' | 'fullscreen'

/**
 * Editor routes that render UnifiedAiPanel: section, question, document /
 * full response (shape-dispatched on `/document`), the Excel schedule and the
 * buyer Word form.
 */
const EDITOR_ROUTE = /^\/app\/bids\/[^/]+\/responses\/[^/]+\/(?:sections\/[^/]+|questionnaire\/[^/]+|document|schedule|form)\/?$/
const BID_ROUTE = /^\/app\/bids\/([^/]+)(?:\/|$)/
const ASSISTANT_ROUTE = /^\/app\/assistant(?:\/|$)/

export function getAssistantDockMode(pathname: string): AssistantDockMode {
  if (ASSISTANT_ROUTE.test(pathname) || EDITOR_ROUTE.test(pathname)) return { kind: 'hidden' }
  const bidMatch = BID_ROUTE.exec(pathname)
  if (bidMatch?.[1]) return { kind: 'bid', bidId: decodeURIComponent(bidMatch[1]) }
  return { kind: 'org' }
}

/** Largest dock width the window allows: min(560, 40% of the window), never under the minimum. */
export function maxDockWidthFor(viewportWidth: number): number {
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) return MAX_DOCK_WIDTH
  return Math.max(MIN_DOCK_WIDTH, Math.min(MAX_DOCK_WIDTH, Math.floor(viewportWidth * MAX_DOCK_VIEWPORT_SHARE)))
}

/** The width to render. The stored preference is untouched, so widening the window brings it back. */
export function clampDockWidth(width: number, viewportWidth: number): number {
  const max = maxDockWidthFor(viewportWidth)
  if (!Number.isFinite(width)) return Math.min(DEFAULT_DOCK_WIDTH, max)
  return Math.min(max, Math.max(MIN_DOCK_WIDTH, Math.round(width)))
}

export interface DockLayoutOptions {
  /** The dock's rendered width. Defaults to the default dock width. */
  dockWidth?: number
  /** Width of the bid menu beside the page: 0 off bid pages. */
  bidMenuWidth?: number
}

/** The page's width once the rail, the bid menu and a pushing dock have taken theirs. */
export function pageContentWidthFor(viewportWidth: number, { dockWidth = DEFAULT_DOCK_WIDTH, bidMenuWidth = 0 }: DockLayoutOptions = {}): number {
  return viewportWidth - APP_RAIL_WIDTH - bidMenuWidth - dockWidth
}

/**
 * Full-screen under 768px. Otherwise the dock pushes the page only when the
 * window is at least 1280px AND the page keeps at least 720px beside it;
 * anything narrower gets the overlay, so a bid page with the menu expanded
 * never squeezes to an unreadable column.
 */
export function dockLayoutFor(viewportWidth: number, options: DockLayoutOptions = {}): AssistantDockLayout {
  if (viewportWidth <= FULLSCREEN_MAX_VIEWPORT) return 'fullscreen'
  if (viewportWidth >= DOCKED_MIN_VIEWPORT && pageContentWidthFor(viewportWidth, options) >= MIN_PAGE_CONTENT_WIDTH) {
    return 'docked'
  }
  return 'overlay'
}
